import {
    PRODUCER_ROW_FIELD_LABELS,
    ProducerRow,
    ProducerRowErrors,
    ProducerRowField,
    normalizeEmail,
} from './producerRows';

/** Producer fields the API reports validation failures against. */
const POINTER_FIELDS: ProducerRowField[] = [ 'producerFirm', 'contactName', 'email', 'phone' ];

const POINTER_PREFIX = '#/producerList/';

interface ParsedDetail {
    index: number | null;
    field: ProducerRowField | null;
    /** Email named in brackets, when the message carries one. */
    email: string | null;
    message: string;
}

/**
 * Parses one entry of a `ValidationFailure.details` list into the row and field it concerns.
 *
 * The API emits three shapes for producer failures, and they are not consistent:
 *   - `#/producerList/0/email: [a@b.com] is not a valid email address`  (pointer, colon)
 *   - `#/producerList/0/contactName must not be empty`                  (pointer, no colon)
 *   - `[a@b.com] is a duplicate email`                                  (no pointer at all)
 *
 * Returns null for anything that is not a producer failure.
 */
export function parseProducerValidationDetail(detail: string): ParsedDetail | null {
    const bracketed = /\[([^\]]+)\]/.exec(detail);
    const email = bracketed ? bracketed[1] : null;

    if (!detail.startsWith(POINTER_PREFIX)) {
        // Only the duplicate-email failure arrives without a pointer; it is attributable
        // solely by the address it names.
        return email && detail.includes('duplicate email')
            ? { index: null, field: 'email', email, message: detail }
            : null;
    }

    const remainder = detail.substring(POINTER_PREFIX.length);
    const separatorMatch = /^(\d+)\/([A-Za-z]+)(?::\s*|\s+)(.*)$/.exec(remainder);
    if (!separatorMatch) {
        return null;
    }

    const [ , rawIndex, rawField, message ] = separatorMatch;
    const field = POINTER_FIELDS.find((candidate) => candidate === rawField) || null;
    return field ? { index: Number(rawIndex), field, email, message } : null;
}

function humanizeMessage(field: ProducerRowField, message: string): string {
    if (message.includes('uses a personal or disposable domain')) {
        return 'Business email address required.';
    }
    if (message.includes('must not be empty')) {
        return `${PRODUCER_ROW_FIELD_LABELS[field]} is required.`;
    }
    if (message.includes('is not a valid email address')) {
        return 'Enter a valid email address.';
    }
    if (message.includes('duplicate email')) {
        return 'This email is already used by another producer.';
    }
    return message;
}

/**
 * Maps a `ValidationFailure.details` list from `PUT /insureds` onto the producer rows that caused
 * it, keyed by row key.
 *
 * A detail that names an email is matched on that address rather than on its list index: the
 * duplicate-email failure carries no index at all, and matching on the address keeps the error on
 * the right row even when the submitted list and the on-screen rows have drifted apart.
 */
export function mapProducerValidationDetails(
    details: string[] = [], rows: ProducerRow[],
): Record<string, ProducerRowErrors> {
    const result: Record<string, ProducerRowErrors> = {};

    const assign = (row: ProducerRow | undefined, field: ProducerRowField, message: string) => {
        if (row) {
            result[row.key] = { ...result[row.key], [field]: humanizeMessage(field, message) };
        }
    };

    details.forEach((detail) => {
        const parsed = parseProducerValidationDetail(detail);
        if (!parsed || !parsed.field) {
            return;
        }

        if (parsed.email) {
            const normalized = normalizeEmail(parsed.email);
            const matched = rows.filter((row) => normalizeEmail(row.email) === normalized);
            if (matched.length > 0) {
                matched.forEach((row) => assign(row, parsed.field as ProducerRowField, parsed.message));
                return;
            }
        }

        assign(parsed.index === null ? undefined : rows[parsed.index], parsed.field, parsed.message);
    });

    return result;
}

/** Whether any of the failure details concern the producer list at all. */
export function hasProducerValidationDetails(details: string[] = []): boolean {
    return details.some((detail) => parseProducerValidationDetail(detail) !== null);
}

/**
 * Flags every row whose email the API reported as a blocked (personal/disposable) domain.
 *
 * Used for the pre-save check, which answers with plain addresses rather than the pointer-prefixed
 * details a refused save returns.
 */
export function mapBlockedProducerEmails(
    blockedEmails: string[], rows: ProducerRow[],
): Record<string, ProducerRowErrors> {
    const blocked = new Set(blockedEmails.map(normalizeEmail));
    const result: Record<string, ProducerRowErrors> = {};
    rows.forEach((row) => {
        if (row.email && blocked.has(normalizeEmail(row.email))) {
            result[row.key] = { email: 'Business email address required.' };
        }
    });
    return result;
}
